import {
  ReviewCashSummary,
  ReviewFinancialBreakdown,
  ReviewFlexMonthlySummary,
  ReviewLoanSummary,
  ReviewMonthlySummary,
  ReviewOfferProduct,
  ReviewPrepaidSummary,
} from "../../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import {
  financialProductDisplayText,
  type FinancialProduct,
} from "../../../constants/financialProducts";
import { FlexMonthlyPaymentDetails } from "./FlexMonthlyPaymentDetails";
import { LoanPaymentDetails } from "./LoanPaymentDetails";
import { MonthlyPaymentDetails } from "./MonthlyPaymentDetails";
import { PrepaidPaymentDetails } from "./PrepaidPaymentDetails";
import { type PromotionSelectionItem } from "services/promotions";
import { OfferReviewPromotionPills } from "components/organisms/OfferReviewPromotionPills";
import { OfferMessages } from "components/molecules/OfferMessages";
import { SelfSupplyGraphLink } from "components/molecules/offerReviewComparison/SelfSupplySeasonGraphLink";

type SimplifiedOfferReviewPaymentDetailsProps = {
  financialBreakdown: ReviewFinancialBreakdown;
  consumption: number;
  production: number;
  hasBattery?: boolean;
  products?: ReviewOfferProduct[];
  isNewHomes?: boolean;
  promotions?: PromotionSelectionItem[];
  omitInlineMonthlyAutopayDisclaimer?: boolean;
  hideOfferExpiryFootnote?: boolean;
  showSelfSupplyGraphLink?: boolean;
  onSelfSupplyGraphClick?: () => void;
};

const SimplifiedOfferReviewPaymentDetails = ({
  financialBreakdown,
  consumption,
  production,
  products,
  isNewHomes = false,
  omitInlineMonthlyAutopayDisclaimer = false,
  promotions,
  hasBattery = false,
  showSelfSupplyGraphLink = false,
  onSelfSupplyGraphClick,
  hideOfferExpiryFootnote = false,
}: SimplifiedOfferReviewPaymentDetailsProps) => {
  const summary = financialBreakdown.summary;

  if (!summary?.type) {
    return null;
  }

  const showValidUntil =
    Boolean(summary.expires) && !isNewHomes && !hideOfferExpiryFootnote;
  const productLabel =
    financialProductDisplayText[summary.type as FinancialProduct] ??
    summary.type;
  const usesPrepaidLayout =
    summary.type === "Prepaid" || summary.type === "Cash";

  const footer = (
    <>
      {promotions?.length ? (
        <OfferReviewPromotionPills promotions={promotions} />
      ) : null}
      <OfferMessages summary={summary} hasBattery={hasBattery ?? false} />
      {showSelfSupplyGraphLink &&
      onSelfSupplyGraphClick &&
      !usesPrepaidLayout ? (
        <SelfSupplyGraphLink onClick={onSelfSupplyGraphClick} />
      ) : null}
    </>
  );

  switch (summary.type) {
    case "Prepaid":
    case "Cash":
      return (
        <PrepaidPaymentDetails
          summary={summary as ReviewPrepaidSummary | ReviewCashSummary}
          includedTotal={financialBreakdown.includedTotal}
          showValidUntil={showValidUntil}
          onSelfSupplyGraphClick={
            showSelfSupplyGraphLink ? onSelfSupplyGraphClick : undefined
          }
        >
          {footer}
        </PrepaidPaymentDetails>
      );
    case "FlexMonthly":
      return (
        <FlexMonthlyPaymentDetails
          summary={summary as ReviewFlexMonthlySummary}
          consumption={consumption}
          production={production}
          products={products}
          showValidUntil={showValidUntil}
        >
          {footer}
        </FlexMonthlyPaymentDetails>
      );
    case "Monthly":
      return (
        <MonthlyPaymentDetails
          summary={summary as ReviewMonthlySummary}
          products={products}
          isNewHomes={isNewHomes}
          omitInlineMonthlyAutopayDisclaimer={
            omitInlineMonthlyAutopayDisclaimer
          }
          showValidUntil={showValidUntil}
        >
          {footer}
        </MonthlyPaymentDetails>
      );
    case "Loan":
      return (
        <LoanPaymentDetails
          summary={summary as ReviewLoanSummary}
          productLabel={productLabel}
          showValidUntil={showValidUntil}
        >
          {footer}
        </LoanPaymentDetails>
      );
    default:
      return null;
  }
};

export { SimplifiedOfferReviewPaymentDetails };
